/**
 * AppErrorBoundary — top-level render-time error backstop.
 *
 * Mounted in `App.js` just inside `<BrowserRouter>` and just outside
 * `<AppShell>`. Catches any error thrown during render of the route
 * tree or its descendants so a single broken page never blanks the
 * whole app.
 *
 * Triggered in the wild by P0 incident `2026-06-29 01:50` — a missing
 * `import ProviderPluckonomicsPage from ...` in App.js produced a
 * `ReferenceError: ProviderPluckonomicsPage is not defined` at render
 * time, which (with no boundary in place) unmounted `#root` entirely
 * and white-screened every user on every URL.
 *
 * Requirements:
 *  - Class component (error boundaries must be classes — React still
 *    has no functional equivalent of `componentDidCatch` as of 19.x).
 *  - Branded fallback UI; self-contained (NO imports from AppShell /
 *    nav / sidebar / context — those may be exactly what threw).
 *  - Inline styles + brand colors only — bypassing the global stylesheet
 *    in case CSS itself is part of what broke.
 *  - "Reload" button that calls `window.location.reload()`.
 *  - The boundary's own logic MUST NOT throw — defensive try/catch
 *    around the console-logging path.
 */
import React from "react";

const PP_ORANGE = "#F89623";
const PP_SLATE = "#374751";
const PP_CREAM = "#FFFBF5";

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Switch to fallback render path. No props/state from the error
    // itself stored here — we only need a boolean. The error detail is
    // captured by `componentDidCatch` for console logging.
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Defensive: the boundary itself must never throw. Wrap the logger.
    try {
      // eslint-disable-next-line no-console
      console.error(
        "[AppErrorBoundary] caught render error:",
        error,
        "componentStack:",
        info && info.componentStack,
      );
    } catch (_loggerErr) {
      // Swallow — there is nowhere to escalate if console itself fails.
    }
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch (_e) {
      // Last-resort no-op if `window` is unavailable (server-side
      // render, jsdom-without-reload, etc.).
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // Self-contained branded fallback. NO classNames from the project
    // stylesheet — if Tailwind or the design tokens broke we still
    // want this to render. Brand palette inlined.
    return (
      <div
        data-testid="app-error-boundary-fallback"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: PP_CREAM,
          fontFamily:
            "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
          padding: "24px",
        }}
      >
        <div
          style={{
            maxWidth: "480px",
            textAlign: "center",
            background: "#FFFFFF",
            border: `1px solid ${PP_ORANGE}33`,
            borderRadius: "16px",
            padding: "40px 32px",
            boxShadow: "0 10px 30px rgba(55, 71, 81, 0.08)",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              fontSize: "44px",
              lineHeight: "1",
              marginBottom: "16px",
            }}
          >
            🐾
          </div>
          <h1
            data-testid="app-error-boundary-title"
            style={{
              color: PP_SLATE,
              fontSize: "22px",
              fontWeight: 600,
              margin: "0 0 12px 0",
            }}
          >
            Something went wrong on our end
          </h1>
          <p
            style={{
              color: PP_SLATE,
              opacity: 0.75,
              fontSize: "15px",
              lineHeight: "1.5",
              margin: "0 0 24px 0",
            }}
          >
            We&apos;re on it. Try reloading the page — if it keeps
            happening, please let us know.
          </p>
          <button
            data-testid="app-error-boundary-reload"
            onClick={this.handleReload}
            style={{
              background: PP_ORANGE,
              color: "#FFFFFF",
              border: "none",
              borderRadius: "9999px",
              padding: "12px 28px",
              fontSize: "15px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "transform 120ms ease, filter 120ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.filter = "brightness(1.05)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = "";
              e.currentTarget.style.transform = "";
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
